<template>
  <SlotLink
    v-if="computedValues.link"
    class="block-teaser-blog-card__link-wrapper"
    :url="computedValues.link"
  >
    <div
      class="block-teaser-blog-card"
      :class="{ 'is-first': isFirstArticle }"
    >
      <AtomImage
        v-if="computedValues.image"
        class="block-teaser-blog-card__image"
        :src="computedValues.image"
        :width="400"
      />
      <div class="block-teaser-blog-card__date">
        {{ computedValues.date }}
      </div>
      <div
        v-if="computedValues.title"
        class="block-teaser-blog-card__title"
        :class="{ 'is-first': isFirstArticle }"
      >
        {{ computedValues.title }}
      </div>
      <div
        v-if="computedValues.author || expert.name"
        class="block-teaser-blog-card__author"
      >
        <AtomImage
          v-if="expert.image"
          :src="expert.image"
        />

        <p v-if="expert.name || computedValues.author">
          {{ useTranslation('general', 'from') }}
        </p>

        <SlotLink
          v-if="expert.name"
          :url="expert.full_slug"
          class="block-teaser-blog-card__author-link"
        >
          {{ expert.name }}
        </SlotLink>
        <p v-if="computedValues.author && !expert.name">
          {{ computedValues.author }}
        </p>
      </div>
      <div class="block-teaser-blog-card__lead">
        <p v-if="computedValues.textTeaser">
          {{ computedValues.textTeaser }}
        </p>
        <p v-else>
          {{ computedValues.lead }}
        </p>
      </div>
      <SlotLink
        v-if="computedValues.link"
        class="block-teaser-blog-card__link"
        :url="computedValues.link"
      >
        {{ useTranslation('general', 'read_article') }}
      </SlotLink>
    </div>
  </Slotlink>
</template>

<script setup>
const props = defineProps({
    data: {
        type: Object,
        default: () => {},
    },
    isFirstArticle: {
        type: Boolean,
        default: false,
    },
});

const { dayjs } = useDayJs();

const storyblokApi = useStoryblokApi();
const expert = ref({});
const getExpert = async (uuid) => {
    if (uuid === '') return;
    try {
        const { data } = await storyblokApi.get('cdn/stories/', {
            starts_with: 'experten/',
            version: 'published',
            by_uuids: uuid.toString(),
        });

        const expertData = data?.stories[0];
        expert.value = expertData;
        const strapiData = JSON.parse(expertData.content.strapi_data);
        expert.value.image = strapiData?.block_author_image?.url;
    } catch (e) {
        useSentryError(e);
    }
};

const computedValues = computed(() => {
    if (props.data) {
        const date = dayjs(props.data.first_published_at).format('MMMM YYYY') || '';
        const image = props.data.content?.blog_preview_image.filename || '';
        const title = props.data.content?.title || '';
        const lead = props.data.content?.main_content[0]?.lead || '';
        const textTeaser = props.data.content?.text_teaser || '';
        const link = props.data.full_slug || '';
        const author = props.data.content?.author;
        const uuidExpert = props.data.content?.expert || '';

        getExpert(uuidExpert);

        return {
            date,
            image,
            title,
            lead,
            textTeaser,
            link,
            author,
            uuidExpert,
        };
    }
    return {};
});
</script>

<style lang="scss" scoped>
.block-teaser-blog-card {
    grid-column: span 4;

    &.is-first {
        grid-column: span 6;
    }

    @include mobile {
        margin-bottom: 20px;
        grid-column: span 6;
    }
}

.block-teaser-blog-card__image {
    @include fluid('margin-bottom', 10px, 20px);

    overflow: hidden;
    border-radius: 10px;
}

.block-teaser-blog-card__title {
    @include typo-font('black');
    @include fluid('font-size', 24px, 30px);
    @include fluid('margin-bottom', 10px, 20px);

    color: $C_GREEN_DARK;

    &.is-first {
        @include typo-size('h2');
    }
}

.block-teaser-blog-card__date {
    @include typo-font('regular');
    @include typo-size('nav');

    margin-bottom: 10px;
    color: $C_GREY_DARK;
}

.block-teaser-blog-card__author {
    @include typo-font('regular');
    @include typo-size('a');

    display: flex;
    align-items: center;
    margin-bottom: 10px;
    color: $C_GREEN_DARK;
    column-gap: 8px;
}

.block-teaser-blog-card__author-link {
    color: $C_PRIMARY;
    transition: color 0.15s ease-in-out;

    &:hover {
        color: $C_SECONDARY;
    }
}

.block-teaser-blog-card__lead {
    @include typo-font('light');
    @include typo-size('lead');
    @include fluid('margin-bottom', 6px, 20px);

    color: $C_GREEN_DARK;
}

.block-teaser-blog-card__link {
    @include typo-font('italic');
    @include typo-size('a');

    color: $C_PRIMARY;
    transition: color 0.15s ease-in-out;
}

.block-teaser-blog-card__link-wrapper {
    &:hover {
        .block-teaser-blog-card__link {
            color: $C_SECONDARY;
        }
    }
}
</style>
